import { setActiveOrg } from '@myrentalmate/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { homePathFor, ROLE_LABELS, type Me } from './me';

export interface ViewChoice {
  key: string;
  label: string;
  isCurrent: boolean;
  choose: () => Promise<void>;
}

/**
 * The views this one login really has: each is a real membership with its own role, or platform
 * admin, and the API enforces it. So the resident view is what a resident gets, not a staff user
 * peeking. Empty for anyone with one view, and during a support session, which decides the org
 * until it's ended from its banner.
 */
export function useViews(me: Me): ViewChoice[] {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inPlatform = pathname.startsWith('/platform');
  if (me.support || me.memberships.length + (me.is_platform_admin ? 1 : 0) < 2) return [];

  const views: ViewChoice[] = me.memberships.map((m) => ({
    key: `org-${m.org_id}`,
    label: `${m.org_name}: ${ROLE_LABELS[m.role]}`,
    isCurrent: !inPlatform && m.org_id === me.active_org_id,
    choose: async () => {
      await setActiveOrg({ org_id: m.org_id });
      // Nothing from the previous view may linger on screen.
      queryClient.clear();
      navigate(homePathFor(m.role));
    },
  }));
  if (me.is_platform_admin) {
    views.push({
      key: 'platform',
      label: 'Platform admin',
      isCurrent: inPlatform,
      choose: async () => navigate('/platform'),
    });
  }
  return views;
}
